import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ColorPickerModule } from 'ngx-color-picker';
import { FonctionsService } from 'src/app/services/fonctions-partagees.service';
import { HttpService } from 'src/app/services/http.service';
import { UtilisateurService } from 'src/app/services/utilisateur.service';

interface GeologyTab {
  key: string;
  translateId: string;
  entityType: string;
  endpoint: string;
  createEndpoint: string;
  updateEndpoint: string;
  columns: { label: string; property: string }[];
  newItemDefaults: Record<string, any>;
}

@Component({
  selector: 'app-site-geology-gui',
  templateUrl: './site-geology-gui.component.html',
  styleUrls: ['./site-geology-gui.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, ColorPickerModule]
})
export class SiteGeologyGuiComponent implements OnInit {
  tab = signal(1);
  items = signal<any[]>([]);
  newItem = signal<Record<string, any>>({});
  isFooterFocus = signal(false);
  showErrorNew = signal(false);
  showErrorModif = signal(false);
  editingIndex = signal(-1);
  oldItemBackup: any = null;

  readonly tabs: GeologyTab[] = [
    {
      key: 'drilllog',
      translateId: 'GEOLOGY.DRILL_LOG',
      entityType: 'discontinuity',
      endpoint: 'discontinuities',
      createEndpoint: 'discontinuity',
      updateEndpoint: 'UpdateDiscontinuity',
      columns: [
        { label: 'GLOBAL.IDENTIFICATEUR', property: 'id' },
        { label: 'GLOBAL.NOM', property: 'name' },
        { label: 'PRODUCTS.NAMESHORT', property: 'shortName' },
        { label: 'PRODUCTS.TYPECOLOR', property: 'color' }
      ],
      newItemDefaults: { name: '', shortName: '', color: '#FFFFFF' }
    },
    {
      key: 'holecondition',
      translateId: 'GEOLOGY.HOLE_CONDITION',
      entityType: 'holecondition',
      endpoint: 'holeconditions',
      createEndpoint: 'holecondition',
      updateEndpoint: 'UpdateHoleCondition',
      columns: [
        { label: 'GLOBAL.IDENTIFICATEUR', property: 'id' },
        { label: 'PRODUCTS.NAMESHORT', property: 'shortName' },
        { label: 'GLOBAL.DESCRIPTION', property: 'description' }
      ],
      newItemDefaults: { shortName: '', description: '' }
    },
    {
      key: 'lithology',
      translateId: 'GEOLOGY.LITHOLOGY',
      entityType: 'lithology',
      endpoint: 'lithologies',
      createEndpoint: 'lithology',
      updateEndpoint: 'UpdateLithology',
      columns: [
        { label: 'GLOBAL.IDENTIFICATEUR', property: 'id' },
        { label: 'GLOBAL.NOM', property: 'name' },
        { label: 'PRODUCTS.CODE', property: 'code' },
        { label: 'GLOBAL.ROCK_TYPE', property: 'rockType' },
        { label: 'GLOBAL.DESCRIPTION', property: 'description' },
        { label: 'PRODUCTS.TYPECOLOR', property: 'color' }
      ],
      newItemDefaults: { name: '', code: '', rockType: '', description: '', color: '#FFFFFF' }
    },
    {
      key: 'mineralisation',
      translateId: 'GEOLOGY.MINERALISATION',
      entityType: 'mineralisation',
      endpoint: 'mineralisations',
      createEndpoint: 'mineralisation',
      updateEndpoint: 'UpdateMineralisation',
      columns: [
        { label: 'GLOBAL.IDENTIFICATEUR', property: 'id' },
        { label: 'GLOBAL.NOM', property: 'name' },
        { label: 'PRODUCTS.NAMESHORT', property: 'shortName' }
      ],
      newItemDefaults: { name: '', shortName: '' }
    }
  ];

  constructor(
    private readonly http: HttpService,
    public utilService: UtilisateurService,
    private readonly fonctionsService: FonctionsService
  ) {}

  ngOnInit(): void {
    this.resetNewItem();
    this.loadItems();
  }

  get currentTab(): GeologyTab {
    return this.tabs[this.tab() - 1];
  }

  get editableColumns(): { label: string; property: string }[] {
    return this.currentTab.columns.filter(c => c.property !== 'id');
  }

  protected changerTab(numTab: number) {
    this.tab.set(numTab);
    this.cancelEdit();
    this.resetNewItem();
    this.loadItems();
  }

  protected getScope(item: any): string {
    return item.siteId ? 'site' : 'area';
  }

  protected toggleItem(item: any) {
    const newState = !item.isActive;
    const url = `Geology/site/toggle/${this.currentTab.entityType}/${item.id}?isActive=${newState}`;

    this.http.put(url, {}).subscribe({
      next: () => {
        item.isActive = newState;
        this.items.set([...this.items()]);
      },
      error: () => {
        this.fonctionsService.launch_toast(false);
      }
    });
  }

  protected selectSiteItem(item: any, index: number) {
    if (!item.siteId) return;
    if (this.editingIndex() === index) return;
    this.cancelEdit();
    this.oldItemBackup = { ...item };
    this.editingIndex.set(index);
  }

  protected updateSiteItem(item: any, index: number) {
    const hasEmpty = this.editableColumns.some(c => c.property !== 'color' && !item[c.property] && item[c.property] !== 0);
    if (hasEmpty) {
      this.showErrorModif.set(true);
      setTimeout(() => this.showErrorModif.set(false), 1500);
      return;
    }

    this.fonctionsService.loading_screen(true);
    const url = `Geology/${this.currentTab.updateEndpoint}`;

    this.http.put(url, item).subscribe({
      next: () => {
        this.editingIndex.set(-1);
        this.oldItemBackup = null;
        this.fonctionsService.launch_toast(true);
        this.fonctionsService.loading_screen(false);
      },
      error: () => {
        this.fonctionsService.launch_toast(false);
        this.fonctionsService.loading_screen(false);
      }
    });
  }

  protected cancelEdit() {
    if (this.editingIndex() >= 0 && this.oldItemBackup) {
      const currentItems = this.items();
      currentItems[this.editingIndex()] = this.oldItemBackup;
      this.items.set([...currentItems]);
    }
    this.editingIndex.set(-1);
    this.oldItemBackup = null;
  }

  protected createSiteItem() {
    const siteId = this.utilService.currentSite()?.siteId;
    if (!siteId) return;

    const item = this.newItem();
    const hasEmpty = this.editableColumns.some(c => c.property !== 'color' && !item[c.property] && item[c.property] !== 0);
    if (hasEmpty) {
      this.showErrorNew.set(true);
      setTimeout(() => this.showErrorNew.set(false), 1500);
      return;
    }

    this.fonctionsService.loading_screen(true);
    const url = `Geology/site/${siteId}/${this.currentTab.createEndpoint}`;

    this.http.post(url, item).subscribe({
      next: () => {
        this.resetNewItem();
        this.loadItems();
        this.fonctionsService.launch_toast(true);
      },
      error: () => {
        this.fonctionsService.launch_toast(false);
        this.fonctionsService.loading_screen(false);
      }
    });
  }

  protected deleteSiteItem(item: any) {
    const siteId = this.utilService.currentSite()?.siteId;
    if (!siteId) return;

    this.fonctionsService.loading_screen(true);
    const url = `Geology/site/${siteId}/${this.currentTab.entityType}/${item.id}`;

    this.http.delete(url).subscribe({
      next: () => {
        this.items.set(this.items().filter(i => i !== item));
        this.fonctionsService.launch_toast(true);
        this.fonctionsService.loading_screen(false);
      },
      error: () => {
        this.fonctionsService.launch_toast(false);
        this.fonctionsService.loading_screen(false);
      }
    });
  }

  protected onFooterFocus() {
    this.isFooterFocus.set(true);
  }

  protected onFooterBlur() {
    this.isFooterFocus.set(false);
  }

  protected getItemProperty(item: any, property: string): any {
    return item[property];
  }

  protected isColorColumn(property: string): boolean {
    return property === 'color';
  }

  protected resetNewItem() {
    this.newItem.set({ ...this.currentTab.newItemDefaults });
  }

  private loadItems() {
    const siteId = this.utilService.currentSite()?.siteId;
    if (!siteId) return;

    this.fonctionsService.loading_screen(true);
    const url = `Geology/site/${siteId}/${this.currentTab.endpoint}`;

    this.http.get<any[]>(url).subscribe({
      next: result => {
        this.items.set(result);
      },
      error: () => {
        this.fonctionsService.launch_toast(false);
        this.fonctionsService.loading_screen(false);
      },
      complete: () => {
        this.fonctionsService.loading_screen(false);
      }
    });
  }
}
